import type { InterludeResult } from '../fight/Interlude';
import type { GameResult } from './types';

export interface Achievement {
  id: string;
  title: string;
  description: string;
  emoji: string;
  /** Hidden achievements show as ??? until unlocked. */
  secret?: boolean;
  /** Earned during a shift… */
  unlocked?: (r: GameResult) => boolean;
  /** …or at a shift-change brawl. */
  unlockedFight?: (r: InterludeResult) => boolean;
}

export const ACHIEVEMENTS: readonly Achievement[] = [
  {
    id: 'clockedIn',
    title: 'Clocked In',
    description: 'Survive a full shift, 10 PM to 6 AM.',
    emoji: '🕕',
    unlocked: (r) => r.reason === 'shiftComplete',
  },
  {
    id: 'indexGreen',
    title: 'Index: Green',
    description: 'Finish a shift without the Index ever leaving green.',
    emoji: '🟩',
    unlocked: (r) =>
      r.reason === 'shiftComplete' &&
      r.stats.hourlyIndex.length > 0 &&
      r.stats.hourlyIndex.every((v) => v >= 50),
  },
  {
    id: 'superhero',
    title: 'Superhero Reflexes',
    description: 'Catch a thrown chair. The internet will call you a superhero.',
    emoji: '🪑',
    unlocked: (r) => r.stats.chairsCaught > 0,
  },
  {
    id: 'thereIsNoChair',
    title: 'There Is No Chair',
    description: 'Catch a chair with perfect timing.',
    emoji: '🕶️',
    unlocked: (r) => r.stats.perfectCatches > 0,
  },
  {
    id: 'unbothered',
    title: 'Completely Unbothered',
    description: 'Serve 4 orders during a single night of brawling. Never look up.',
    emoji: '😐',
    unlocked: (r) => r.stats.unbothered >= 4,
  },
  {
    id: 'allTheWay',
    title: 'All The Way',
    description: 'Serve perfect hash browns with all eight toppings.',
    emoji: '🥔',
    unlocked: (r) => r.stats.allTheWay > 0,
  },
  {
    id: 'femaApproved',
    title: 'FEMA Approved',
    description: 'Keep the Waffle House Index green through a hurricane.',
    emoji: '🌀',
    unlocked: (r) => r.femaVerdict === 'green',
  },
  {
    id: 'theyEatAtWaffleHouse',
    title: 'They Said Yes',
    description: 'Nail the proposal order.',
    emoji: '💍',
    unlocked: (r) => r.proposalYes === true,
  },
  {
    id: 'viral',
    title: 'Going Viral',
    description: 'Rack up 1,000,000 views on a stream.',
    emoji: '📱',
    unlocked: (r) => r.stats.views >= 1_000_000,
  },
  {
    id: 'combo10',
    title: 'In The Zone',
    description: 'Serve 10 perfect orders in a row.',
    emoji: '🔥',
    unlocked: (r) => r.stats.bestCombo >= 10,
  },
  {
    id: 'bigTipper',
    title: 'Bills Paid',
    description: 'Earn $300 in tips in one shift.',
    emoji: '💵',
    unlocked: (r) => r.tips >= 300,
  },
  {
    id: 'sir',
    title: 'Sir, This Is A Waffle House',
    description: 'Witness it firsthand.',
    emoji: '🗣️',
    unlocked: (r) => r.sawSirThisIsAWaffleHouse,
  },
  {
    id: 'secretShopper',
    title: 'Secret Shopper',
    description: 'Doze off long enough for a customer to cook his own meal.',
    emoji: '💤',
    secret: true,
    unlocked: (r) => r.secretShopper,
  },
  {
    id: 'tinyBandit',
    title: 'Tiny Bandit',
    description: 'Serve a very small, very polite customer.',
    emoji: '🦝',
    secret: true,
    unlocked: (r) => r.raccoonServed,
  },
  {
    id: 'femaNotified',
    title: 'FEMA Has Been Notified',
    description: 'Let the Waffle House Index hit red. The unthinkable.',
    emoji: '🟥',
    secret: true,
    unlocked: (r) => r.reason === 'closed',
  },
  {
    id: 'newHost',
    title: 'The New Host',
    description: 'The Waffle House has found its new host.',
    emoji: '👁️',
    secret: true,
    unlocked: (r) => r.hostPleased,
  },
];

export const FIGHT_ACHIEVEMENTS: readonly Achievement[] = [
  {
    id: 'deescalation',
    title: 'De-escalation Expert',
    description: 'Calm down all three troublemakers at a shift change.',
    emoji: '🕊️',
    unlockedFight: (r) => r.calmed === r.total && r.total > 0,
  },
  {
    id: 'flawlessService',
    title: 'Flawless Service',
    description: 'Calm a customer down without losing any of your cool.',
    emoji: '🧼',
    unlockedFight: (r) => r.bouts.some((b) => b.flawless),
  },
  {
    id: 'superScattered',
    title: 'Scattered, Smothered, Super’d',
    description: 'Land the ALL THE WAY super.',
    emoji: '💥',
    unlockedFight: (r) => r.stats.supers > 0,
  },
  {
    id: 'caughtItAgain',
    title: 'Caught It. Again.',
    description: 'Catch a chair mid-brawl with a perfectly timed block.',
    emoji: '🪑',
    secret: true,
    unlockedFight: (r) => r.stats.chairCatches > 0,
  },
  {
    id: 'lostCool',
    title: 'Lost Your Cool',
    description: 'Get out-rowdied at a shift change. It happens.',
    emoji: '😤',
    secret: true,
    unlockedFight: (r) => r.bouts.some((b) => b.outcome === 'lost'),
  },
];

/** Every achievement, for the achievements screen. */
export const ALL_ACHIEVEMENTS: readonly Achievement[] = [...ACHIEVEMENTS, ...FIGHT_ACHIEVEMENTS];

export function newlyUnlocked(result: GameResult, already: ReadonlySet<string>): Achievement[] {
  return ACHIEVEMENTS.filter((a) => !already.has(a.id) && a.unlocked?.(result) === true);
}

export function newlyUnlockedFight(
  result: InterludeResult,
  already: ReadonlySet<string>,
): Achievement[] {
  return FIGHT_ACHIEVEMENTS.filter((a) => !already.has(a.id) && a.unlockedFight?.(result) === true);
}
